import { paymentSchema, } from './dsl.js';
const NEGATIVE = new Set([-1]);
const ZERO = new Set([0]);
const POSITIVE = new Set([1]);
const NON_NEGATIVE = new Set([0, 1]);
const SOME_SIGN = new Set([-1, 0, 1]);
function signOf(value) {
    if (value > 0)
        return POSITIVE;
    if (value < 0)
        return NEGATIVE;
    return ZERO;
}
function combine(a, b, op) {
    const result = new Set();
    for (const x of a) {
        for (const y of b) {
            for (const sign of op(x, y))
                result.add(sign);
        }
    }
    return result;
}
function product(a, b) {
    return combine(a, b, (x, y) => signOf(x * y));
}
function sum(a, b) {
    return combine(a, b, (x, y) => (x === -y ? (x === 0 ? ZERO : SOME_SIGN) : signOf(x + y)));
}
function lesser(a, b) {
    return combine(a, b, (x, y) => signOf(Math.min(x, y)));
}
function greater(a, b) {
    return combine(a, b, (x, y) => signOf(Math.max(x, y)));
}
function union(a, b) {
    return new Set([...a, ...b]);
}
function signsOfObservable(observable) {
    switch (observable.kind) {
        case 'const':
            return signOf(observable.value);
        case 'daysLate':
            return NON_NEGATIVE;
        case 'context':
            return SOME_SIGN;
        case 'multiply':
            return product(signsOfObservable(observable.left), signsOfObservable(observable.right));
        case 'max':
        case 'min': {
            const [first, ...rest] = observable.values;
            if (!first)
                return ZERO;
            const fold = observable.kind === 'max' ? greater : lesser;
            return rest.reduce((signs, value) => fold(signs, signsOfObservable(value)), signsOfObservable(first));
        }
    }
}
function signsOfAmount(amount) {
    switch (amount.kind) {
        case 'unit':
            return POSITIVE;
        case 'scale':
            return product(signsOfObservable(amount.factor), signsOfAmount(amount.amount));
        case 'give':
            return product(NEGATIVE, signsOfAmount(amount.amount));
        case 'when':
            return union(ZERO, signsOfAmount(amount.amount));
        case 'describe':
            return signsOfAmount(amount.amount);
        case 'capped':
            return lesser(signsOfAmount(amount.amount), signsOfObservable(amount.maxAmount));
        case 'surcharge': {
            const base = signsOfAmount(amount.amount);
            return sum(base, product(signsOfObservable(amount.rate), base));
        }
        case 'all':
            return amount.amounts.reduce((signs, part) => sum(signs, signsOfAmount(part)), ZERO);
        case 'first':
            return amount.amounts.reduce((signs, part) => union(signs, signsOfAmount(part)), ZERO);
    }
}
function amountCanBePositive(amount) {
    return signsOfAmount(amount).has(1);
}
export function paysSomething(payment) {
    switch (payment.kind) {
        case 'zero':
            return false;
        case 'once':
            return amountCanBePositive(payment.amount);
        case 'repeat':
            return payment.maxOccurrences !== 0 && amountCanBePositive(payment.amount);
        case 'all':
        case 'first':
            return payment.payments.some(paysSomething);
        case 'notToExceed':
            return payment.maxAmount > 0 && paysSomething(payment.payment);
    }
}
function amountIsCategorised(amount) {
    switch (amount.kind) {
        case 'describe':
            return amount.classification?.kind === 'taxonomy' || amountIsCategorised(amount.amount);
        case 'unit':
            return false;
        case 'give':
        case 'scale':
        case 'capped':
        case 'when':
            return amountIsCategorised(amount.amount);
        case 'surcharge':
            return amount.classification.kind === 'taxonomy' && amountIsCategorised(amount.amount);
        case 'all':
        case 'first':
            return amount.amounts.every(amountIsCategorised);
    }
}
export function everyPaymentIsCategorised(payment) {
    switch (payment.kind) {
        case 'zero':
            return true;
        case 'once':
        case 'repeat':
            return amountIsCategorised(payment.amount);
        case 'all':
        case 'first':
            return payment.payments.every(everyPaymentIsCategorised);
        case 'notToExceed':
            return everyPaymentIsCategorised(payment.payment);
    }
}
export function neverEnds(payment) {
    switch (payment.kind) {
        case 'zero':
        case 'once':
            return false;
        case 'repeat':
            return payment.endDate === undefined && payment.maxOccurrences === undefined;
        case 'all':
        case 'first':
            return payment.payments.some(neverEnds);
        case 'notToExceed':
            return neverEnds(payment.payment);
    }
}
export function conflictingLabel(payment) {
    switch (payment.kind) {
        case 'zero':
            return undefined;
        case 'once':
        case 'repeat':
            return labelUnder(payment.amount, undefined);
        case 'all':
        case 'first': {
            for (const part of payment.payments) {
                const found = conflictingLabel(part);
                if (found !== undefined)
                    return found;
            }
            return undefined;
        }
        case 'notToExceed':
            return conflictingLabel(payment.payment);
    }
}
function labelUnder(amount, outer) {
    switch (amount.kind) {
        case 'describe': {
            if (outer !== undefined && outer !== amount.description) {
                return { outer, inner: amount.description };
            }
            return labelUnder(amount.amount, amount.description);
        }
        case 'scale':
        case 'capped':
        case 'when':
        case 'give':
            return labelUnder(amount.amount, outer);
        case 'surcharge':
            return labelUnder(amount.amount, undefined);
        case 'all':
        case 'first': {
            for (const part of amount.amounts) {
                const found = labelUnder(part, undefined);
                if (found !== undefined)
                    return found;
            }
            return undefined;
        }
        case 'unit':
            return undefined;
    }
}
export function chargeNames(payment) {
    switch (payment.kind) {
        case 'zero':
            return [];
        case 'once':
        case 'repeat':
            return namesOfAmount(payment.amount);
        case 'all':
        case 'first':
            return payment.payments.flatMap(chargeNames);
        case 'notToExceed':
            return chargeNames(payment.payment);
    }
}
function namesOfAmount(amount) {
    switch (amount.kind) {
        case 'describe':
        case 'surcharge':
            return [amount.description, ...namesOfAmount(amount.amount)];
        case 'scale':
        case 'capped':
        case 'when':
        case 'give':
            return namesOfAmount(amount.amount);
        case 'all':
        case 'first':
            return amount.amounts.flatMap(namesOfAmount);
        case 'unit':
            return [];
    }
}
function amountClassifications(amount) {
    switch (amount.kind) {
        case 'describe': {
            const here = amount.classification === undefined ? [] : [amount.classification];
            return [...here, ...amountClassifications(amount.amount)];
        }
        case 'surcharge':
            return [amount.classification, ...amountClassifications(amount.amount)];
        case 'unit':
            return [];
        case 'scale':
        case 'capped':
        case 'when':
        case 'give':
            return amountClassifications(amount.amount);
        case 'all':
        case 'first':
            return amount.amounts.flatMap(amountClassifications);
    }
}
export function classificationsOf(payment) {
    switch (payment.kind) {
        case 'zero':
            return [];
        case 'once':
        case 'repeat':
            return amountClassifications(payment.amount);
        case 'all':
        case 'first':
            return payment.payments.flatMap(classificationsOf);
        case 'notToExceed':
            return classificationsOf(payment.payment);
    }
}
export function taxonomyRefsOf(payment) {
    return classificationsOf(payment).flatMap((classification) => classification.kind === 'taxonomy'
        ? [{ category: classification.category, subcategory: classification.subcategory }]
        : []);
}
export const categorisedPaymentSchema = paymentSchema
    .refine(everyPaymentIsCategorised, {
    message: 'Every payment has to name a TEO Taxonomy category and subcategory.',
})
    .transform((payment) => payment);
//# sourceMappingURL=checks.js.map